import { inject, Injectable } from '@angular/core';
import { PreInitSettingsService } from './settings/pre-init-setting.service';
import { add } from 'date-fns';
import { catchError, map, Observable, of, shareReplay, tap, throwError } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { ADPResourceService } from 'digital-core';
import { ApiPlanResponse, BulkClaim, BulkPlanDetailsResponse, Certificate, Claim, ClaimStatusKeys, Coverage, Equipment, PaymentHistoryItem, PlanDetail, PlanTier } from 'src/app/model/plan/plan-detail';
import { AppConstants } from 'src/app/core/constants';
import { CacheEntry } from 'src/app/model/cache-entry/cache-entry';
import { IPlan } from 'src/app/model/portal-profile/IPortalProfile';
import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root'
})
export class PlanService {
  private preInitSettingsService = inject(PreInitSettingsService);
  private httpClient = inject(HttpClient);
  private storageService = inject(StorageService);
  private resourceService = inject(ADPResourceService);

  private middlewareUrl: string;
  private accountPath: string;
  private readonly DEFAULT_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds
  private goodPlanTierTag = '';
  private betterPlanTierTag = '';
  private bestPlanTierTag = '';
  private listingPlanTierTag = '';
  private cacheDuration: number = this.DEFAULT_CACHE_DURATION;
  private disabledAddonsDealerCodes: string[] = [];
  private mappDealerCodes: string[] = [];

  constructor() {
    this.middlewareUrl = this.preInitSettingsService.getSettings().middleWare!.middlewareUrl;
    this.accountPath = this.preInitSettingsService.getSettings().middleWare!.accountPath;
  }

  initialize() {
    if (this.cacheDuration) {
      return;
    }

    const cacheDurationValue = this.resourceService.getPortalSetting('Global_Site_Cache_Duration')?.Value;
    if (cacheDurationValue) {
      const parsedDuration = parseInt(cacheDurationValue, 10);
      if (!isNaN(parsedDuration) && parsedDuration > 0) {
        this.cacheDuration = parsedDuration * 60 * 1000; // convert minutes to milliseconds
      }
    }
  }

  // `plan` is widened to IPlan (not ISelectedPlan) so callers can refresh a policy the user
  // does not currently have selected - buildPlanDetail only reads IPlan fields off it.
  getPlanDetails(planNumber: string, plan: IPlan, refresh?: boolean | null, dealerCode?: string | null): Observable<PlanDetail> {
    this.initialize();
    const cacheKey = `${AppConstants.STORAGE_KEYS.PlanDetail}:${planNumber}`;
    const cacheEntry = this.storageService.getSession<CacheEntry<PlanDetail>>(cacheKey);

    // Check if cache exists, is not being forced to refresh, and is still valid
    if (cacheEntry && refresh !== true && CacheEntry.isCacheValid(cacheEntry.timestamp, this.cacheDuration)) {
      return of(cacheEntry.data);
    }

    const method = AppConstants.API_ENDPOINTS.PlanDetails;
    const dealerCodeParam = dealerCode ? `&dealerCode=${dealerCode}` : '';

    return this.httpClient.get<ApiPlanResponse[]>(`${this.middlewareUrl}${this.accountPath}${method}?planNumber=${planNumber}&refresh=${refresh ?? false}${dealerCodeParam}`).pipe(
      map((res) => {
        try {
          const planDetail = this.buildPlanDetail(res[0], plan);
          if (!planDetail) {
            throw new Error(`Failed to build plan detail for plan number: ${planNumber}`);
          }
          return planDetail;
        } catch (error) {
          throw new Error(`Failed to build plan detail for plan number: ${planNumber}`, { cause: error });
        }
      }),
      tap((planDetail) => {
        if (planDetail) {
          const entry = new CacheEntry(planDetail);
          this.storageService.setSession<CacheEntry<PlanDetail>>(cacheKey, entry);
        }
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  getAllPlanDetails(plans: IPlan[], refresh?: boolean | null): Observable<PlanDetail[]> {
    this.initialize();
    const allCacheKey = `${AppConstants.STORAGE_KEYS.PlanDetail}:all`;
    const cacheEntry = this.storageService.getSession<CacheEntry<PlanDetail[]>>(allCacheKey);

    if (cacheEntry && refresh !== true && CacheEntry.isCacheValid(cacheEntry.timestamp, this.cacheDuration)) {
      return of(cacheEntry.data);
    }

    const method = AppConstants.API_ENDPOINTS.PlanDetails;

    return this.httpClient.get<ApiPlanResponse[]>(`${this.middlewareUrl}${this.accountPath}${method}?refresh=${refresh ?? false}`).pipe(
      map((responses) =>
        responses
          .map((res) => {
            const matchingPlan = plans.find((p) => p.planNumber === res.planNumber) ?? null;
            try {
              return this.buildPlanDetail(res, matchingPlan);
            } catch (error) {
              // Isolate a single malformed plan (e.g. missing certificates) so one bad
              // record doesn't fail the whole batch and log the user out.
              console.error(`Failed to build plan detail for plan number: ${res.planNumber}`, error);
              return null;
            }
          })
          .filter((d): d is PlanDetail => d !== null)
      ),
      tap((planDetails) => {
        if (planDetails.length) {
          this.storageService.setSession<CacheEntry<PlanDetail[]>>(allCacheKey, new CacheEntry(planDetails));
          planDetails.forEach((pd) => {
            this.storageService.setSession<CacheEntry<PlanDetail>>(`${AppConstants.STORAGE_KEYS.PlanDetail}:${pd.planNumber}`, new CacheEntry(pd));
          });
        }
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  getBulkPlanDetails(plans: IPlan[], refresh?: boolean | null): Observable<{ planDetails: PlanDetail[]; count: number; claims: BulkClaim[] }> {
    this.initialize();
    const cacheKey = `${AppConstants.STORAGE_KEYS.PlanDetail}:bulk`;
    const cacheEntry = this.storageService.getSession<CacheEntry<{ planDetails: PlanDetail[]; count: number; claims: BulkClaim[] }>>(cacheKey);

    if (cacheEntry && refresh !== true && CacheEntry.isCacheValid(cacheEntry.timestamp, this.cacheDuration)) {
      return of(cacheEntry.data);
    }

    const method = AppConstants.API_ENDPOINTS.BulkPlanDetails;

    return this.httpClient.get<BulkPlanDetailsResponse>(`${this.middlewareUrl}${this.accountPath}${method}?refresh=${refresh ?? false}`).pipe(
      map((response) => ({
        planDetails: response.plans.data
          .map((res) => {
            const matchingPlan = plans.find((p) => p.planNumber === res.planNumber) ?? null;
            try {
              return this.buildPlanDetail(res, matchingPlan);
            } catch (error) {
              // Isolate a single malformed plan (e.g. missing certificates) so one bad
              // record doesn't fail the whole batch and log the user out.
              console.error(`Failed to build plan detail for plan number: ${res.planNumber}`, error);
              return null;
            }
          })
          .filter((d): d is PlanDetail => d !== null),
        count: response.plans.count,
        claims: response.claims,
      })),
      tap(({ planDetails, count, claims }) => {
        if (planDetails.length) {
          this.storageService.setSession(cacheKey, new CacheEntry({ planDetails, count, claims }));
          planDetails.forEach((pd) => {
            this.storageService.setSession<CacheEntry<PlanDetail>>(`${AppConstants.STORAGE_KEYS.PlanDetail}:${pd.planNumber}`, new CacheEntry(pd));
          });
        }
      }),
      catchError((error) => {
        // Re-throw: LoggedInUserSettings callers own the failure handling (handlePlanDetailsFetchFailure).
        console.error('Failed to fetch bulk plan details', error);
        return throwError(() => error);
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  /**
   * Maps the bulkplandetails endpoint's root-level `claims` (one list spanning every policy)
   * into the `Claim` shape the rest of the app already renders. The live wire already uses the
   * Claim field names (`actionsSummary.actionsCount`, `fulfillment.fulfillmentTypeEnum`); the
   * legacy `actionsSummary.count` / `fulfillment.fulfillmentTypeCode` names are only fallbacks.
   */
  mapBulkClaimsToClaims(bulkClaims: BulkClaim[] | null | undefined): Claim[] {
    return (bulkClaims ?? []).map((bulkClaim) => this.mapBulkClaimToClaim(bulkClaim));
  }

  private mapBulkClaimToClaim(bulkClaim: BulkClaim): Claim {
    return {
      certificate: bulkClaim.certificate,
      claimNumber: bulkClaim.claimNumber,
      status: bulkClaim.status,
      lossInfo: {
        coverageTypeCode: bulkClaim.lossInfo?.coverageTypeCode,
        dateOfLoss: bulkClaim.lossInfo?.dateOfLoss,
        reportedDate: bulkClaim.lossInfo?.reportedDate,
        problemDescription: bulkClaim.lossInfo?.problemDescription ?? '',
      },
      claimedEquipment: bulkClaim.claimedEquipment,
      actionsSummary: {
        actionsCount: bulkClaim.actionsSummary?.actionsCount ?? bulkClaim.actionsSummary?.count ?? 0,
        lastActionCode: bulkClaim.actionsSummary?.lastActionCode,
      },
      fulfillment: bulkClaim.fulfillment
        ? {
            fulfillmentType: bulkClaim.fulfillment.fulfillmentType,
            fulfillmentTypeEnum: bulkClaim.fulfillment.fulfillmentTypeEnum ?? bulkClaim.fulfillment.fulfillmentTypeCode ?? '',
            typeCode: bulkClaim.fulfillment.typeCode,
            entityType: bulkClaim.fulfillment.entityType,
            statusDate: bulkClaim.fulfillment.statusDate,
            statusReason: bulkClaim.fulfillment.statusReason,
          }
        : undefined,
      properties: bulkClaim.properties ?? [],
    };
  }

  private buildPlanDetail(raw: ApiPlanResponse, plan: IPlan | null): PlanDetail | null {
    if (!raw) {
      return null;
    }

    // ApiPlanResponse.certificates is typed as required, but the bulk/all-plans endpoints
    // can still return a plan with it missing/null at runtime — normalize once so every
    // access below is safe instead of throwing and failing the whole batch.
    const certificates = this.restrictAllowedDeviceTypesToRegisteredEquipment(raw.certificates ?? [], raw.isMultiCertPolicy);

    if (this.disabledAddonsDealerCodes.length === 0) {
      const disabledAddonsSetting = this.resourceService.getPortalSetting('Global_Disabled_Addons_Dealer_Codes')?.Value;
      if (disabledAddonsSetting) {
        this.disabledAddonsDealerCodes = disabledAddonsSetting.split(',').map((code) => code.trim());
      }
    }

    const equipmentList: Equipment[] = [];

    // 1) PlanType from planCode prefix
    const planType: PlanTier = this.getPlanType(raw.planCode);

    // helpers
    const planCoreConst = this.resourceService.getPortalLabel('Global_Core_Plan_Details_Prefix').Value;
    const planNonCoreConst = this.resourceService.getPortalLabel('Global_NonCore_Plan_Details_Prefix').Value;
    const isCore = (c: Certificate) => c.certificateNumber.startsWith(planCoreConst);
    const isNonCore = (c: Certificate) => c.certificateNumber.startsWith(planNonCoreConst);

    // 2) TotalLiabilityLimit = liabilityLimit from the first CORE-* certificate
    const allowDefaultCorePlan = this.resourceService.getPortalSetting('Global_Core_Plan_Allow_Default')?.Value === 'true';
    const coreCertificates = certificates.filter(isCore);
    if (coreCertificates.length === 0 && allowDefaultCorePlan && certificates[0]) {
      coreCertificates.push(certificates[0]);
    }
    const coreCert = coreCertificates[0];
    const nonCoreCert = certificates.find(isNonCore);
    const totalLiabilityLimit = coreCert?.product?.liabilityLimit ?? 0;

    // 3) DeviceTypeCodes = unique union of:
    //    - all allowedDeviceTypes from CORE-* certificates
    //    - all equipments[].deviceTypeCode across ALL certificates
    const deviceTypeSet = new Set<string>();
    const coverageTypeSet = new Set<string>();
    
    coreCertificates.forEach(c => {
      (c.product?.allowedDeviceTypes ?? []).forEach(dt => deviceTypeSet.add(dt));
      // loop thru all coverages and get all codes for coverages field
      c.coverages?.forEach((cov: Coverage) => {
        if (cov.coverageTypeCode == 'CV') {
          if (planType == AppConstants.PLAN.PlanTier.Best ) {
            // do not add 'CV' for premium plans
          } else {
            coverageTypeSet.add(cov.coverageTypeCode);
          }
        } else {
          if (cov.coverageTypeCode) {
            coverageTypeSet.add(cov.coverageTypeCode);
          }
        }
      });
    });

    certificates.forEach((c) => {
      if (c.product?.productDescription?.toLowerCase().startsWith('guest')) {
        deviceTypeSet.add('GUEST');
        return;
      }

      // Add allowedDeviceTypes from product
      const allowedTypes = c.product?.allowedDeviceTypes ?? [];
      allowedTypes.forEach((dt) => deviceTypeSet.add(dt));

      (c.equipments ?? []).forEach((eq) => {
        eq.childCertificateNumber = c.certificateNumber; // link back to child certificate
        const state = plan?.customers?.[0]?.addresses?.[0]?.state?.toLowerCase();
        const hiddenDeviceTypeCodes = state ? this.resourceService.getPortalSetting('Global_Location_Hidden_Equipment_DeviceTypeCode_' + state)?.Value : undefined;
        let hiddenCodesArray: string[] = [];
        if (hiddenDeviceTypeCodes) {
          hiddenCodesArray = hiddenDeviceTypeCodes.split(';').map((code: string) => code.trim());
        }
        if (hiddenCodesArray.includes(eq.deviceTypeCode!)) {
          return; // skip adding this equipment
        }
        equipmentList.push(eq);
        if (eq?.deviceTypeCode) deviceTypeSet.add(eq.deviceTypeCode);
      });
    });

    const deviceTypeCodes = Array.from(deviceTypeSet);
    // MP: This is a hack to add some business rules for coverageTypeCodes
    // if ELEC devicetype is preset and SP for surge protection only if the plan is not part of CA state
    if (deviceTypeCodes.indexOf('ELEC') > -1) {
      if (plan?.customers?.[0]?.addresses?.[0]?.state?.toLowerCase() !== 'california') {
        coverageTypeSet.add('SP');
      }
    }

    const coverageTypeCodes = Array.from(coverageTypeSet).filter((ctc) => {
      //filter by plan type, for example we added 'SP' in the previous step, but we want to remove it for certain dealers
      //we also want to be more flexible so we use portal settings to define the dealer codes to filter out
      const filter = this.resourceService.getPortalSetting(`Global_Plan_Details_Remove_${ctc}_By_Plan_Type`)?.Value;
      if (filter) {
        const dealerCodes = filter.split(',').map((code) => code.trim());
        if (dealerCodes.includes(planType)) {
          return false;
        }
      }
      return true;
    });

    // 4) Payment history
    //    - First item: { date: planSalesDate, amount: premium.gross from CORE, planType: 'Plan' }
    //    - Then: group NON-CORE certificates by certificateStartDate,
    //            sum their salesPrice for that date, each becomes an item with planType 'Add-Ons'
    const paymentHistory: PaymentHistoryItem[] = [
      {
        date: raw.planSalesDate,
        amount: coreCert?.premium?.gross ?? 0,
        planType: 'Plan',
      },
    ];

    const addonGroups = new Map<string, number>();
    certificates
      .filter(c => !coreCertificates.includes(c))
      .forEach(c => {
        const key = c.certificateSalesDate;
        addonGroups.set(key, (addonGroups.get(key) ?? 0) + (c.premium?.gross ?? 0));
      });

    // stable order by date ascending (optional, but nice)
    Array.from(addonGroups.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .forEach(([date, amount]) => {
        paymentHistory.push({ date, amount, planType: 'Add-Ons' });
      });

    let coverageMB = coreCert?.coverages?.find((cov: Coverage) => cov.coverageTypeCode === 'MB');
    if (nonCoreCert && !coverageMB?.deductibles?.find(d => !!d?.deductible)) {
      coverageMB = nonCoreCert?.coverages?.find((cov: Coverage) => cov.coverageTypeCode === 'MB');
    }

    //from the coverageMB, get the deductible amount if it exists
    const deductible = coverageMB?.deductibles?.find(d =>
      d.deductibleBasedOnCode === AppConstants.PLAN.Deductible.Fixed &&
      d.methodOfRepairCode === AppConstants.PLAN.Deductible.MethodOfRepairCodeH &&
      AppConstants.PLAN.Deductible.Source?.split('|')?.includes(d.deductibleSource)
    )?.deductible ?? 0;

    // sort by registeredItemName (optional, but nice)
    equipmentList.sort((a, b) =>
      (a.registeredItemName ?? '').localeCompare(b.registeredItemName ?? '', undefined, {
        sensitivity: 'base',
        numeric: true,
      })
    );

    // Return enriched object (non-mutating)
    const result = {
      ...raw,
      // Normalize the per-client wire field into one value every consumer reads via
      // `plan.activeClaims` — see the field comment on ApiPlanResponse for why both exist.
      activeClaims: raw.activeClaims ?? raw.activeServiceRequests ?? null,
      certificates,
      planType,
      totalLiabilityLimit,
      deviceTypeCodes,
      coverageTypeCodes,
      paymentHistory,
      deductible,
      coreCertificate: coreCert?.certificateNumber ?? '',
      nonCoreCertificate: nonCoreCert?.certificateNumber ?? '',
      equipments: equipmentList,
      isAddonsAllowed: this.disabledAddonsDealerCodes.length === 0 || !this.disabledAddonsDealerCodes.includes(raw.dealerCode),
      planStartDate: coverageMB?.beginDate ?? raw.planSalesDate,
      planEndDate: coverageMB?.endDate ?? add(new Date(raw.planSalesDate), { months: raw.planTerm }).toISOString(),
    };

    return result;
  }

  /**
   * A single-cert-style policy binds one certificate to exactly one physical item once it's
   * registered — the customer can't come back and register a DIFFERENT device type against
   * that same certificate afterward. `allowedDeviceTypes` as sold reflects every type the
   * underlying product COULD have covered, which only stays useful while there's still an
   * unregistered/placeholder slot to fill. Once every equipment on the certificate already
   * carries its own deviceTypeCode, there's nothing left to pick, so narrow allowedDeviceTypes
   * down to just what actually got registered — every downstream consumer of `certificates`
   * (this plan's own deviceTypeCodes union below, the appliance category picker,
   * ServiceRequestAggregationService's certificate matching) then sees what's really
   * selectable instead of the product's full theoretical catalog.
   *
   * Multi-cert (MAPP) policies are left untouched: allowedDeviceTypes there still gates which
   * certificate a NEW item should register against, a decision this plan-level flag doesn't
   * disambiguate per certificate.
   */
  private restrictAllowedDeviceTypesToRegisteredEquipment(certificates: Certificate[], isMultiCertPolicy: boolean): Certificate[] {
    if (isMultiCertPolicy) {
      return certificates;
    }

    return certificates.map((certificate) => {
      const equipments = certificate.equipments ?? [];
      const allEquipmentTyped = equipments.length > 0 && equipments.every((equipment) => !!equipment.deviceTypeCode);

      if (!allEquipmentTyped || !certificate.product?.allowedDeviceTypes?.length) {
        return certificate;
      }

      const registeredDeviceTypes = Array.from(new Set(equipments.map((equipment) => equipment.deviceTypeCode!)));

      return { ...certificate, product: { ...certificate.product, allowedDeviceTypes: registeredDeviceTypes } };
    });
  }

  getPlanType(planCode: string) {
    if (!this.goodPlanTierTag || !this.betterPlanTierTag || !this.bestPlanTierTag) {
      this.goodPlanTierTag = this.resourceService.getPortalSetting('Global_Plan_Prefix_Good')?.Value;
      this.betterPlanTierTag = this.resourceService.getPortalSetting('Global_Plan_Prefix_Better')?.Value;
      this.bestPlanTierTag = this.resourceService.getPortalSetting('Global_Plan_Prefix_Best')?.Value;
      this.listingPlanTierTag = this.resourceService.getPortalSetting('Global_Plan_Prefix_Listing')?.Value;
    }

    if (!planCode) {
      return AppConstants.PLAN.PlanTier.Good as PlanTier;
    }

    return planCode.indexOf(this.goodPlanTierTag)>-1 ? AppConstants.PLAN.PlanTier.Good as PlanTier
      : planCode.indexOf(this.betterPlanTierTag)>-1 ? AppConstants.PLAN.PlanTier.Better as PlanTier :
        planCode.indexOf(this.bestPlanTierTag)>-1 ? AppConstants.PLAN.PlanTier.Best as PlanTier :
        planCode.indexOf(this.listingPlanTierTag)>-1 ? AppConstants.PLAN.PlanTier.Listing as PlanTier :
          AppConstants.PLAN.PlanTier.Good as PlanTier;
  }

  /**
   * Display name for a plan, e.g. "Lowe's Protection Plan" or "Essential Protection".
   *
   * Prefers the curated `Global_{segment}_Display_Name` label selected by dealerCode
   * (Multi-Appliance / Protection Plus / regular Protection Plan). These three keys are
   * authored on LOWES ONLY — deliberately not on parent, because the fallback below is what
   * keeps AHW on its tier naming. Authoring them on parent would make AHW inherit them and
   * start showing Lowe's plan names. No client-identity check is needed (or wanted) here:
   * CMS authoring is the gate.
   *
   * The `Global_` prefix matters beyond naming — `getViewLabel` only sees the label prefixes
   * the current route requested via its `sfresources`, and `Global` is the one prefix every
   * route already loads. A feature-scoped prefix would have needed registering on each
   * consuming route (parent AND leaf) or the lookup would silently miss.
   *
   * The tier key can only ever be the FALLBACK here: `planType` is not returned by
   * bulkplandetails, it's derived by getPlanType() above from the planCode prefix
   * (`Global_Plan_Prefix_*`, authored as the AHW codes GOOD/BETTER/BEST/HWSLC) and returns
   * 'Good' for BOTH an empty AND an unmatched planCode. Lowes plan codes match none of those
   * prefixes, so before this method existed every Lowes plan silently resolved to 'Good' and
   * rendered the AHW tier name "Essential Protection".
   *
   * Consumed only by the cancel-plan flow (review + result steps) for now. The same defect
   * exists at the my-plan, billing, plan-renewal and GSSP-handover call sites, which still
   * read `MyPlan_{planType}_Protection_Label` directly — migrate them onto this method when
   * that follow-up is picked up.
   */
  getPlanDisplayName(planType: string | null | undefined, dealerCode: string | null | undefined): string {
    const segment = this.resolvePlanNameSegment(dealerCode);
    const curatedName = this.stripHtml(this.getViewLabelOrEmpty(`Global_${segment}_Display_Name`));
    if (curatedName) {
      return curatedName;
    }

    // stripHtml on the tier branch too: the MyPlan_* values are authored as plain text today,
    // but TermsConditions_Plan_LowesPlus_Name shows CMS authors do wrap these in markup, so
    // don't leave one branch able to leak raw HTML into the summary row.
    return planType ? this.stripHtml(this.getViewLabelOrEmpty(`MyPlan_${planType}_Protection_Label`)) : '';
  }

  /**
   * Curated plan-name segment for a dealerCode. Segment VALUES intentionally match the ones
   * terms-and-conditions.component.ts uses for its own `TermsConditions_Plan_{key}_Name`
   * lookups, but the two key families are independent — that component is untouched here.
   */
  private resolvePlanNameSegment(dealerCode: string | null | undefined): string {
    const code = dealerCode?.trim().toUpperCase() ?? '';
    if (this.dealerCodesFromSetting('Global_MAPP_Dealer_Codes').includes(code)) {
      return 'LowesMultiAppliance';
    }
    if (this.dealerCodesFromSetting('Global_LowesPlus_Dealer_Codes').includes(code)) {
      return 'LowesPlus';
    }
    return 'LowesProtection';
  }

  private dealerCodesFromSetting(settingKey: string): string[] {
    return (this.resourceService.getPortalSetting(settingKey)?.Value ?? '')
      .split(',')
      .map((code) => code.trim().toUpperCase())
      .filter(Boolean);
  }

  /**
   * `getViewLabel` returns the literal string `"<key> null"` — not '' and not null — when the
   * key isn't in the current route's loaded label set, so a plain truthiness test would read a
   * miss as a hit and never fall back. Mirrors terms-and-conditions' `getSettingValue` guard.
   */
  private getViewLabelOrEmpty(key: string): string {
    const value = this.resourceService.getViewLabel(key) ?? '';
    return value.endsWith(' null') ? '' : value;
  }

  private stripHtml(value: string): string {
    return value?.replace(/<[^>]*>/g, '').trim() ?? '';
  }

  getTypeKey(claim: Claim, type: ClaimStatusKeys): string {
    const prefix = claim?.fulfillment?.entityType && claim?.fulfillment?.typeCode
      ? this.getPrefixKey(claim) : this.getDefaultPrefixKey(claim);
    return `${prefix}_${type}`;
  }

  getPrefixKey(claim?: Claim): string {
    if (claim) {
      let isActionPresent = claim?.actionsSummary?.actionsCount > 0;
      let entityType = claim?.fulfillment?.entityType;
      let typeCode = claim?.fulfillment?.typeCode;

      let baseKey = this.getClaimTrackDetailsKeyPrefix(claim?.fulfillment?.fulfillmentTypeEnum, claim?.status, isActionPresent, claim?.lossInfo?.coverageTypeCode);

      return `${baseKey}_${entityType}_${typeCode}`;
    }
    return '';
  }

  getDefaultTypeKey(claim: Claim, type: ClaimStatusKeys): string {
    const prefix = this.getDefaultPrefixKey(claim);
    return `${prefix}_${type}`;
  }

  getDefaultPrefixKey(claim: Claim): string {
    const status = claim?.status ?? '';
    const isActionPresent = claim?.actionsSummary?.actionsCount > 0;

    if (status?.toLowerCase() === 'pending') {
      return `${status}_${isActionPresent ? 'T' : 'F'}`;
    }
    return status;
  }

  getClaimTrackDetailsKeyPrefix(
    fulfillmentTypeEnum: string,
    status: string,
    isActionPresent: boolean,
    coverageTypeCode: string
  ): string {
    return `${fulfillmentTypeEnum}_${status}_${isActionPresent ? 'T' : 'F'}_${coverageTypeCode}`;
  }
}
